import React, { useState, useRef, useLayoutEffect } from "react"; // Added hooks
import { useTranslation } from "react-i18next";
import imageSrc from "../../assets/images/phone-onboarding.png";
import imageSrcIcon1 from "../../assets/images/icon-community.svg";
import imageSrcIcon2 from "../../assets/images/icon-direct-messaging.svg";
import imageSrcIcon3 from "../../assets/images/icon-calendar.svg";
import imageSrcIcon4 from "../../assets/images/icon-requests.svg";
import imageSrcIcon5 from "../../assets/images/icon-favorites.svg";
import imageSrcIcon6 from "../../assets/images/icon-ratings-reviews.svg";

// Define the style type (Same as in Services.tsx)
type TransformStyle = {
    transform: string;
    opacity: number;
};

// Initial/Neutral State (Same as in Services.tsx)
const NEUTRAL_TRANSFORM = 'translateY(0px)';
const COMMON_STYLE = {
    transition: 'transform 0.6s ease-out, opacity 0.6s ease-out', // Slower transition for a smooth reveal
    willChange: 'transform, opacity',
};

const Features: React.FC = () => {
  const { t } = useTranslation();

  // Ref for the entire section (to track scroll position)
  const featuresRef = useRef<HTMLElement>(null);

  // State for the image's dynamic style
  const [imageStyle, setImageStyle] = useState<TransformStyle>({ transform: NEUTRAL_TRANSFORM, opacity: 0 });

  useLayoutEffect(() => {

    const handleScroll = () => {
      const currentSection = featuresRef.current;
      if (!currentSection) {
        return;
      }

      const rect = currentSection.getBoundingClientRect();
      const viewportHeight = window.innerHeight;

      // Calculate how far the section has moved into the viewport
      // 0 = section top is at viewport bottom (just visible)
      // 1 = section bottom is at viewport bottom (fully visible)
      const scrollProgress = 1 - (rect.top / viewportHeight);

      // Clamp the progress between 0 and 1
      const clampedProgress = Math.min(1, Math.max(0, scrollProgress));


      // --- Reveal Effect Logic ---

      // Start position: hidden 150px below final position, and fully transparent
      const START_OFFSET = 150;

      // Calculate the current offset (goes from 150 to 0)
      const currentOffset = START_OFFSET * (1 - clampedProgress);

      // Calculate the opacity (goes from 0 to 1)
      const currentOpacity = clampedProgress;


      // --- Reset logic for when element is out of view (going back up) ---
      // If the component is mostly out of view (e.g., less than 10% visible)
      if (clampedProgress < 0.1) {
          setImageStyle({ transform: `translateY(${START_OFFSET}px)`, opacity: 0 });
          return;
      }

      // Set the dynamic style
      setImageStyle({
          transform: `translateY(${currentOffset}px)`,
          opacity: currentOpacity
      });
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Initial check

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <section id="features"
      ref={featuresRef}
      className="banner neumorphic-container rounded-0 py-5 mb-0"
    >
      <div className="container col-lg-12">
        <h2 className="text-center">{t("features.title")}</h2>
        <p className="text-center">{t("features.subtitle")}</p>
      </div>
      <div className="row justify-content-center align-items-center">
        <div className="col-12 col-md-4">
          <div className="neumorphic-container mb-4 d-flex justify-content-center align-items-center p-3">
            <div className="me-3">
              <div className="testimonial-image-container neumorphic-container p-3 rounded-4 overflow-hidden">
                <img className="testimonial-image" src={imageSrcIcon1} alt="testimonial"/>
              </div>
            </div>
            <div>
              <h3 className="gradient-text">{t("features.community.title")}</h3>
              <p>{t("features.community.description")}</p>
            </div>
          </div>
          <div className="neumorphic-container mb-4 d-flex justify-content-center align-items-center p-3">
            <div className="me-3">
              <div className="testimonial-image-container neumorphic-container p-3 rounded-4 overflow-hidden">
                <img className="testimonial-image" src={imageSrcIcon2} alt="testimonial"/>
              </div>
            </div>
            <div>
              <h3 className="gradient-text">{t("features.messaging.title")}</h3>
              <p>{t("features.messaging.description")}</p>
            </div>
          </div>
          <div className="neumorphic-container mb-4 d-flex justify-content-center align-items-center p-3">
            <div className="me-3">
              <div className="testimonial-image-container neumorphic-container p-3 rounded-4 overflow-hidden">
                <img className="testimonial-image" src={imageSrcIcon3} alt="testimonial"/>
              </div>
            </div>
            <div>
              <h3 className="gradient-text">{t("features.calendar.title")}</h3>
              <p>{t("features.calendar.description")}</p>
            </div>
          </div>
        </div>
        <div className="col-12 col-md-4 d-flex justify-content-center align-items-center">
          <div className="neumorphic-container oval oval-xs d-flex justify-content-center align-items-center my-4">
            <div className="neumorphic-container  oval oval-xxs overflow-hidden position-relative">
              <img src={imageSrc} className="col-12 position-absolute-lg"
                alt="logo-white"
                style={{ ...COMMON_STYLE, ...imageStyle }}
              />
            </div>
          </div>
        </div>
        <div className="col-12 col-md-4">
          <div className="neumorphic-container mb-4 d-flex justify-content-center align-items-center p-3">
            <div className="me-3">
              <div className="testimonial-image-container neumorphic-container p-3 rounded-4 overflow-hidden">
                <img className="testimonial-image" src={imageSrcIcon4} alt="testimonial"/>
              </div>
            </div>
            <div>
              <h3 className="gradient-text">{t("features.requests.title")}</h3>
              <p>{t("features.requests.description")}</p>
            </div>
          </div>
          <div className="neumorphic-container mb-4 d-flex justify-content-center align-items-center p-3">
            <div className="me-3">
              <div className="testimonial-image-container neumorphic-container p-3 rounded-4 overflow-hidden">
                <img className="testimonial-image" src={imageSrcIcon5} alt="testimonial"/>
              </div>
            </div>
            <div>
              <h3 className="gradient-text">{t("features.favorites.title")}</h3>
              <p>{t("features.favorites.description")}</p>
            </div>
          </div>
          <div className="neumorphic-container mb-4 d-flex justify-content-center align-items-center p-3">
            <div className="me-3">
              <div className="testimonial-image-container neumorphic-container p-3 rounded-4 overflow-hidden">
                <img className="testimonial-image" src={imageSrcIcon6} alt="testimonial"/>
              </div>
            </div>
            <div>
              <h3 className="gradient-text">{t("features.reviews.title")}</h3>
              <p>{t("features.reviews.description")}</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default Features;
